import React, { useState } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import { Close } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { marketingColor, marketingFont } from '../../theme/marketingTokens';
import { marketingEvent, isMarketingEventCurrent } from '../../constants/marketingEvent';

const DISMISSED_KEY = 'motzu.announcement.dismissed';

/** Storage is unavailable in private windows and with site data blocked. */
const readDismissed = (): boolean => {
  try {
    return window.localStorage.getItem(DISMISSED_KEY) === marketingEvent.name;
  } catch {
    return false;
  }
};

const persistDismissed = () => {
  try {
    window.localStorage.setItem(DISMISSED_KEY, marketingEvent.name);
  } catch {
    /* Non-fatal: the bar simply returns on the next visit. */
  }
};

/**
 * Launch announcement above the header.
 *
 * The handoff's bar is permanent; per its own note this one is dismissible and
 * retires itself once the show closes, so nobody has to remember to pull it.
 */
export const AnnouncementBar: React.FC<{ onBookMeeting: () => void }> = ({ onBookMeeting }) => {
  const { t } = useTranslation() as { t: (key: string) => string };
  const [dismissed, setDismissed] = useState(readDismissed);

  if (dismissed || !isMarketingEventCurrent()) return null;

  const handleDismiss = () => {
    setDismissed(true);
    persistDismissed();
  };

  return (
    <Box
      sx={{
        backgroundColor: marketingColor.accent,
        color: marketingColor.ink,
        px: { xs: 2, md: 6 },
        py: 1.25,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1.75,
        flexWrap: 'wrap',
        position: 'relative',
      }}
    >
      <Box
        component="span"
        sx={{
          fontFamily: marketingFont.mono,
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          border: '1px solid rgba(10,20,24,0.35)',
          borderRadius: '3px',
          px: 1, 
          py: 0.4,
        }}
      >
        {t('public.announcement.chip')}
      </Box>
      <Typography
        component="span"
        sx={{ fontFamily: marketingFont.display, fontSize: 13.5, fontWeight: 700 }}
      >
        {t('public.announcement.message')} — {marketingEvent.datesShort} ·{' '}
        {marketingEvent.venue}, {marketingEvent.cityState}
      </Typography>
      <Box
        component="button"
        type="button"
        onClick={onBookMeeting}
        sx={{
          fontFamily: marketingFont.mono,
          fontSize: 11.5,
          fontWeight: 700,
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
          color: marketingColor.ink,
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          textDecoration: 'underline',
          p: 0,
        }}
      >
        {t('public.announcement.cta')}
      </Box>
      <IconButton
        size="small"
        onClick={handleDismiss}
        aria-label={t('public.announcement.dismiss')}
        sx={{
          position: { xs: 'static', md: 'absolute' },
          right: 12,
          color: marketingColor.ink,
          '&:hover': { color: marketingColor.ink, backgroundColor: 'rgba(10,20,24,0.12)' },
        }}
      >
        <Close sx={{ fontSize: 16 }} />
      </IconButton>
    </Box>
  );
};
